import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Trash2, ArrowLeft, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/security")({
  component: SecurityPage,
  head: () => ({ meta: [{ title: "Security & 2FA — MissionReady" }] }),
});

type Factor = {
  id: string;
  friendly_name?: string | null;
  factor_type: string;
  status: "verified" | "unverified";
  created_at: string;
};

type Enroll = {
  factorId: string;
  qrSvg: string;
  secret: string;
  uri: string;
};

function SecurityPage() {
  const { user, currentRole } = useAuth();
  const [factors, setFactors] = useState<Factor[]>([]);
  const [loading, setLoading] = useState(true);
  const [aal, setAal] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<Enroll | null>(null);
  const [friendly, setFriendly] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [{ data: fac }, { data: a }] = await Promise.all([
      supabase.auth.mfa.listFactors(),
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    ]);
    setFactors((fac?.all ?? []) as Factor[]);
    setAal(a?.currentLevel ?? null);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const verified = factors.filter((f) => f.status === "verified" && f.factor_type === "totp");
  const has2fa = verified.length > 0;

  const startEnroll = async () => {
    setBusy(true);
    // Clean up any stale unverified factors first
    for (const f of factors.filter((x) => x.status === "unverified")) {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: friendly || `Authenticator ${new Date().toLocaleDateString()}`,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    setEnroll({
      factorId: data.id,
      qrSvg: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    });
  };

  const verifyEnroll = async () => {
    if (!enroll) return;
    setBusy(true);
    const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId: enroll.factorId });
    if (cErr) { setBusy(false); return toast.error(cErr.message); }
    const { error } = await supabase.auth.mfa.verify({
      factorId: enroll.factorId,
      challengeId: ch.id,
      code: code.trim(),
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Two-factor authentication enabled");
    setEnroll(null);
    setCode("");
    setFriendly("");
    refresh();
  };

  const cancelEnroll = async () => {
    if (!enroll) return;
    await supabase.auth.mfa.unenroll({ factorId: enroll.factorId });
    setEnroll(null);
    setCode("");
    refresh();
  };

  const removeFactor = async (id: string) => {
    if (!confirm("Remove this authenticator? You'll need to re-enroll to use 2FA again.")) return;
    const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
    if (error) return toast.error(error.message);
    toast.success("Authenticator removed");
    refresh();
  };

  const elevate = async (factorId: string) => {
    const code = prompt("Enter the 6-digit code from your authenticator app");
    if (!code) return;
    const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
    if (cErr) return toast.error(cErr.message);
    const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code: code.trim() });
    if (error) return toast.error(error.message);
    toast.success("Session elevated to AAL2");
    refresh();
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <Link to="/settings" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Settings
      </Link>
      <h1 className="mt-2 font-display text-2xl font-bold md:text-3xl">Security & 2FA</h1>
      <p className="text-sm text-muted-foreground">
        {user?.email} · {currentRole} · session AAL: <span className="font-mono">{aal ?? "…"}</span>
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {/* Status card */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-5 w-5 ${has2fa ? "text-emerald-500" : "text-muted-foreground"}`} />
            <h2 className="font-display text-lg font-semibold">Two-factor authentication</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {has2fa
              ? "2FA is enabled. You'll be asked for a code from your authenticator app at sign-in."
              : "Add an extra layer of security. After enabling, you'll need your phone's authenticator app to sign in."}
          </p>
          {loading ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <ul className="mt-4 space-y-2">
              {verified.length === 0 && (
                <li className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                  No authenticators enrolled.
                </li>
              )}
              {verified.map((f) => (
                <li key={f.id} className="flex items-center justify-between rounded-md border border-border bg-background/40 p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{f.friendly_name || "Authenticator"}</div>
                    <div className="text-xs text-muted-foreground">
                      TOTP · added {new Date(f.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {aal !== "aal2" && (
                      <Button size="sm" variant="outline" onClick={() => elevate(f.id)}>Verify now</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeFactor(f.id)} aria-label="Remove">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Enroll card */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <h2 className="font-display text-lg font-semibold">
              {enroll ? "Scan & verify" : "Add an authenticator"}
            </h2>
          </div>

          {!enroll && (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                Use Google Authenticator, 1Password, Authy, or any TOTP-compatible app.
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="friendly">Device name (optional)</Label>
                  <Input
                    id="friendly"
                    placeholder="iPhone 15"
                    className="mt-1"
                    value={friendly}
                    onChange={(e) => setFriendly(e.target.value)}
                  />
                </div>
                <Button onClick={startEnroll} disabled={busy} className="w-full">
                  {busy ? "Starting…" : has2fa ? "Add another authenticator" : "Enable 2FA"}
                </Button>
              </div>
            </>
          )}

          {enroll && (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                Scan the QR code in your authenticator app, then enter the 6-digit code to confirm.
              </p>
              <div className="mt-4 flex justify-center rounded-md bg-white p-3">
                <div
                  className="h-44 w-44 [&_svg]:h-full [&_svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: enroll.qrSvg }}
                />
              </div>
              <div className="mt-3 break-all rounded-md border border-border bg-background/40 p-2 text-center font-mono text-xs">
                {enroll.secret}
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="code">6-digit code</Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="mt-1 text-center font-mono tracking-widest"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={verifyEnroll} disabled={busy || code.length !== 6} className="flex-1">
                    {busy ? "Verifying…" : "Verify & enable"}
                  </Button>
                  <Button variant="ghost" onClick={cancelEnroll} disabled={busy}>Cancel</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
